import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createColumnHelper, flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import type { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { format, differenceInCalendarDays } from 'date-fns';
import { el } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Copy, Download, MoreVertical, Pencil, Store, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Pill } from '~/components/pill';
import { PolicyBadgeThumb } from '~/components/promotion/policy-badge-thumb';
import { ACTION_TYPE_META_MAP } from '~/components/promotion/actions/action-type-meta';
import { computePromotionStatus } from '~/lib/promotion-status';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';
import { cn } from '@/lib/utils';
import { SortableRow } from './grid-row';
import type { PromotionGridRow } from '~/lib/fixtures/demo-grid';

const GRID_COLS = 'grid-cols-[32px_50px_1fr_150px_180px_220px_40px]';
const PAGE_SIZES = [10, 25, 50, 100];

interface GridTableProps {
	rows: PromotionGridRow[];
	pagination: PaginationState;
	onPaginationChange: OnChangeFn<PaginationState>;
	onReorder: (activeId: number, overId: number) => void;
	onRowClick: (id: number) => void;
	onRowAction: (id: number, action: 'edit' | 'duplicate' | 'view' | 'export' | 'delete') => void;
	focusedId?: number;
}

const columnHelper = createColumnHelper<PromotionGridRow>();

export function GridTable({ rows, pagination, onPaginationChange, onReorder, onRowClick, onRowAction, focusedId }: GridTableProps) {
	const { t } = useTranslation();
	const now = new Date();
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

	const columns = useMemo(
		() => [
			columnHelper.display({
				id: 'seq',
				header: '#',
				cell: ({ row }) => <span className="font-mono text-xs text-foreground-subtle">{row.original.priority}</span>
			}),
			columnHelper.display({
				id: 'policy',
				header: toGreekCaps(t('promotion.grid.columns.policy')),
				cell: ({ row }) => {
					const r = row.original;
					return (
						<div className="flex items-center gap-3">
							<PolicyBadgeThumb gradient={r.thumbGradient} icon={r.thumbIcon} size="sm" />
							<div className="min-w-0">
								<p className="truncate text-sm font-medium text-foreground">{r.name}</p>
								<p className="flex items-center gap-1.5 text-xs text-foreground-subtle">
									{r.code && <span className="rounded bg-code px-1 font-mono">{r.code}</span>}
									<span>
										{t('promotion.basicInfo.identity.priority')} {r.priority}
									</span>
								</p>
							</div>
						</div>
					);
				}
			}),
			columnHelper.display({
				id: 'types',
				header: toGreekCaps(t('promotion.grid.columns.types')),
				cell: ({ row }) => {
					const types = row.original.actionTypes;
					const shown = types.slice(0, 3);
					const overflow = types.length - shown.length;
					return (
						<div className="flex items-center gap-1">
							{shown.map(type => {
								const Icon = ACTION_TYPE_META_MAP[type].icon;
								return (
									<span
										key={type}
										className="flex h-[22px] w-[22px] items-center justify-center rounded bg-muted text-foreground-muted"
										title={t(ACTION_TYPE_META_MAP[type].labelKey)}>
										<Icon size={12} />
									</span>
								);
							})}
							{overflow > 0 && (
								<span className="rounded bg-muted px-1 text-[10px] text-foreground-subtle">
									{t('promotion.grid.typesOverflow', { count: overflow })}
								</span>
							)}
						</div>
					);
				}
			}),
			columnHelper.display({
				id: 'validity',
				header: toGreekCaps(t('promotion.grid.columns.validity')),
				cell: ({ row }) => {
					const r = row.original;
					const range =
						r.activeFrom && r.activeTo
							? `${format(r.activeFrom, 'dd MMM yy', { locale: el })} – ${format(r.activeTo, 'dd MMM yy', { locale: el })}`
							: '—';
					let relative: string;
					if (!r.activeFrom && !r.activeTo) relative = t('promotion.grid.validityNoLimit');
					else if (r.activeTo && r.activeTo < now)
						relative = t('promotion.grid.validityAgoDays', { count: differenceInCalendarDays(now, r.activeTo) });
					else if (r.activeFrom && r.activeFrom > now)
						relative = t('promotion.grid.validityInDays', { count: differenceInCalendarDays(r.activeFrom, now) });
					else relative = t('promotion.grid.validityInForce');
					return (
						<div className="text-xs">
							<p className="text-foreground">{range}</p>
							<p className="text-foreground-subtle">{relative}</p>
						</div>
					);
				}
			}),
			columnHelper.display({
				id: 'status',
				header: toGreekCaps(t('promotion.grid.columns.status')),
				cell: ({ row }) => {
					const r = row.original;
					const { activePills, lifecyclePills, visibilityPills } = computePromotionStatus({
						isActive: r.isActive,
						activeFrom: r.activeFrom,
						activeTo: r.activeTo,
						code: r.code,
						actionCount: r.actionTypes.length,
						isVisible: r.isVisible,
						hideFromOffers: r.hideFromOffers,
						now
					});
					return (
						<div className="flex flex-wrap gap-1">
							{[...activePills, ...visibilityPills, ...lifecyclePills].map(p => (
								<Pill key={p.labelKey} variant={p.variant} icon={p.icon}>
									{t(p.labelKey)}
								</Pill>
							))}
						</div>
					);
				}
			}),
			columnHelper.display({
				id: 'actions',
				header: '',
				cell: ({ row }) => (
					<div onClick={e => e.stopPropagation()}>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="ghost" size="icon" type="button" aria-label="more">
									<MoreVertical size={16} />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onSelect={() => onRowAction(row.original.id, 'edit')}>
									<Pencil size={14} /> {t('promotion.grid.rowMenu.edit')}
								</DropdownMenuItem>
								<DropdownMenuItem onSelect={() => onRowAction(row.original.id, 'duplicate')}>
									<Copy size={14} /> {t('promotion.grid.rowMenu.duplicate')}
								</DropdownMenuItem>
								<DropdownMenuItem onSelect={() => onRowAction(row.original.id, 'view')}>
									<Store size={14} /> {t('promotion.grid.rowMenu.viewInStore')}
								</DropdownMenuItem>
								<DropdownMenuItem onSelect={() => onRowAction(row.original.id, 'export')}>
									<Download size={14} /> {t('promotion.grid.rowMenu.exportJson')}
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem variant="destructive" onSelect={() => onRowAction(row.original.id, 'delete')}>
									<Trash2 size={14} /> {t('promotion.grid.rowMenu.delete')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				)
			})
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t]
	);

	const table = useReactTable({
		data: rows,
		columns,
		state: { pagination },
		onPaginationChange,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel()
	});

	const pageRows = table.getRowModel().rows;
	const { pageIndex, pageSize } = pagination;
	const from = rows.length === 0 ? 0 : pageIndex * pageSize + 1;
	const to = Math.min((pageIndex + 1) * pageSize, rows.length);

	const onDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (over && active.id !== over.id) onReorder(Number(active.id), Number(over.id));
	};

	return (
		<div className="overflow-hidden rounded-lg border border-border bg-surface">
			{/* header */}
			<div className={cn('grid items-center gap-2 border-b border-border bg-elevated px-4 py-2', GRID_COLS)}>
				<span />
				{table.getHeaderGroups()[0].headers.map(header => (
					<span key={header.id} className="text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">
						{flexRender(header.column.columnDef.header, header.getContext())}
					</span>
				))}
			</div>

			{/* body */}
			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
				<SortableContext items={pageRows.map(r => r.original.id)} strategy={verticalListSortingStrategy}>
					{pageRows.map(row => (
						<SortableRow
							key={row.original.id}
							id={row.original.id}
							gridCols={GRID_COLS}
							focused={row.original.id === focusedId}
							onClick={() => onRowClick(row.original.id)}>
							{row.getVisibleCells().map(cell => (
								<div key={cell.id} className="min-w-0">
									{flexRender(cell.column.columnDef.cell, cell.getContext())}
								</div>
							))}
						</SortableRow>
					))}
				</SortableContext>
			</DndContext>

			{/* pagination footer */}
			<div className="flex items-center justify-between gap-4 border-t border-border px-4 py-2 text-xs text-foreground-muted">
				<span>{t('promotion.grid.pagination.showing', { from, to, total: rows.length })}</span>
				<div className="flex items-center gap-1">
					<Button
						variant="ghost"
						size="icon"
						type="button"
						disabled={!table.getCanPreviousPage()}
						onClick={() => table.previousPage()}
						aria-label="prev">
						<ChevronLeft size={16} />
					</Button>
					<span className="px-2">
						{pageIndex + 1} / {Math.max(table.getPageCount(), 1)}
					</span>
					<Button
						variant="ghost"
						size="icon"
						type="button"
						disabled={!table.getCanNextPage()}
						onClick={() => table.nextPage()}
						aria-label="next">
						<ChevronRight size={16} />
					</Button>
				</div>
				<Select value={String(pageSize)} onValueChange={v => table.setPageSize(Number(v))}>
					<SelectTrigger className="w-32">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{PAGE_SIZES.map(size => (
							<SelectItem key={size} value={String(size)}>
								{t('promotion.grid.pagination.perPage', { count: size })}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
		</div>
	);
}
